import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle } from "reactstrap";
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { apiURL1 } from '../../../config';
import { useNavigate } from 'react-router-dom';
import Footer from "components/Footers/Footer";

const SuperAdminDashboard = () => {
  const [studentAttendance, setStudentAttendance] = useState({ present: 0, absent: 0, leave: 0 });
  const [teacherAttendance, setTeacherAttendance] = useState({ present: 0, absent: 0, leave: 0 });
  const [feeStatus, setFeeStatus] = useState({
    totalStudents: 0,
    fullyPaid: { count: 0, percentage: "0.00" },
    partiallyPaid: { count: 0, percentage: "0.00" },
    pending: { count: 0, percentage: "0.00" }
  });
  const [incomeExpense, setIncomeExpense] = useState({ totalIncome: 0, totalExpense: 0, netBalance: 0, month: "" });
  const [studentPaymentData, setStudentPaymentData] = useState([]);
  const [incomeExpenseData, setIncomeExpenseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const studentResponse = await axios.get(`${apiURL1}/superadmin/student/attendance/summary/today`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        const teacherResponse = await axios.get(`${apiURL1}/superadmin/teacher/attendance/summary/today`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        const feeResponse = await axios.get(`${apiURL1}/superadmin/getFeeStatusSummary`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        const incomeExpenseResponse = await axios.get(`${apiURL1}/superadmin/simple-month-summary`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        // console.log('studentResponse Response:', studentResponse.data);

        setStudentAttendance({
          present: parseFloat(studentResponse.data.present),
          absent: parseFloat(studentResponse.data.absent),
          leave: parseFloat(studentResponse.data.leave),
        });

        // console.log('Teacher Attendance Response:', teacherResponse.data);

        setTeacherAttendance({
          present: parseFloat(teacherResponse.data.present),
          absent: parseFloat(teacherResponse.data.absent),
          leave: parseFloat(teacherResponse.data.leave),
        });

        setFeeStatus(feeResponse.data);

        setIncomeExpense(incomeExpenseResponse.data);

        // Update student payment data
        setStudentPaymentData([
          { name: 'Fully Paid', value: feeResponse.data.fullyPaid.count, color: '#2dce89' },
          { name: 'Partially Paid', value: feeResponse.data.partiallyPaid.count, color: '#f5a623' },
          { name: 'Pending', value: feeResponse.data.pending.count, color: '#f5365c' },
        ]);

        // Update income expense data
        setIncomeExpenseData([
          { name: 'Income', value: incomeExpenseResponse.data.totalIncome, color: '#2dce89' },
          { name: 'Expense', value: incomeExpenseResponse.data.totalExpense, color: '#f5365c' },
        ]);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const studentAttendanceData = [
    { name: 'Present', value: studentAttendance.present, color: '#2dce89' },
    { name: 'Absent', value: studentAttendance.absent, color: '#f5365c' },
    { name: 'Leave', value: studentAttendance.leave, color: '#172b4d' },
  ];

  const teacherAttendanceData = [
    { name: 'Present', value: teacherAttendance.present, color: '#2dce89' },
    { name: 'Absent', value: teacherAttendance.absent, color: '#f5365c' },
    { name: 'Leave', value: teacherAttendance.leave, color: '#172b4d' },
  ];

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md="6">
          <Card>
            <CardBody onClick={() => navigate('/admin/attendance-mangement')} style={{ cursor: 'pointer' }}>
              <CardTitle tag="h5">Overall Student Attendance (Today)</CardTitle>
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <>
                  <div className="d-flex justify-content-around align-items-center">
                    <div className="text-center">
                      <div className="font-weight-bold text-success">{studentAttendance.present.toFixed(2)} %</div>
                      <div>Present</div>
                    </div>
                    <div className="text-center">
                      <div className="font-weight-bold text-danger">{studentAttendance.absent.toFixed(2)} %</div>
                      <div>Absent</div>
                    </div>
                    <div className="text-center">
                      <div className="font-weight-bold text-info">{studentAttendance.leave.toFixed(2)} %</div>
                      <div>Leave</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={studentAttendanceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {
                          studentAttendanceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))
                        }
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardBody onClick={() => navigate('/admin/attendance-mangement')} style={{ cursor: 'pointer' }}>
              <CardTitle tag="h5">Staff Attendance (Today)</CardTitle>
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <>
                  <div className="d-flex justify-content-around align-items-center">
                    <div className="text-center">
                      <div className="font-weight-bold text-success">{teacherAttendance.present.toFixed(2)} %</div>
                      <div>Present</div>
                    </div>
                    <div className="text-center">
                      <div className="font-weight-bold text-danger">{teacherAttendance.absent.toFixed(2)} %</div>
                      <div>Absent</div>
                    </div>
                    <div className="text-center">
                      <div className="font-weight-bold text-info">{teacherAttendance.leave.toFixed(2)} %</div>
                      <div>Leave</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={teacherAttendanceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {
                          teacherAttendanceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))
                        }
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Fee Status (This Month)</CardTitle>
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <div className="d-flex justify-content-around align-items-center">
                  <div className="text-center">
                    <div className="font-weight-bold text-success">{feeStatus.fullyPaid.count} Students</div>
                    <div>Fully Paid</div>
                  </div>
                  <div className="text-center">
                    <div className="font-weight-bold text-warning">{feeStatus.partiallyPaid.count} Students</div>
                    <div>Partially Paid</div>
                  </div>
                  <div className="text-center">
                    <div className="font-weight-bold text-danger">{feeStatus.pending.count} Students</div>
                    <div>Pending</div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Student Fee Overview</CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={studentPaymentData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {
                      studentPaymentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))
                    }
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <Card>
            <CardBody onClick={() => navigate('/admin/finance-mangement')} style={{ cursor: 'pointer' }}>
              <CardTitle tag="h5">Income vs Expense ({incomeExpense.month})</CardTitle>
              <div className="d-flex justify-content-around align-items-center">
                <div className="text-center">
                  <div className="font-weight-bold text-success">Income: {incomeExpense.totalIncome}</div>
                </div>
                <div className="text-center">
                  <div className="font-weight-bold text-danger">Expense: {incomeExpense.totalExpense}</div>
                </div>
                <div className="text-center">
                  <div className="font-weight-bold text-info">Net Balance: {incomeExpense.netBalance}</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={incomeExpenseData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8">
                    {
                      incomeExpenseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))
                    }
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default SuperAdminDashboard;
